import { useHistory } from 'react-router-dom';
import {
  Box,
  createTheme,
  IconButton,
  Tab,
  Tabs,
  ThemeProvider,
  useMediaQuery
} from '@material-ui/core';
import { ChevronLeft } from '@carbon/icons-react';
import {
  CapaQuotationDetailedQuotation,
  CapaQuotationPartnerStatus
} from '@generated/graphql';
import useDashboard from '@hooks/useDashboard';
import CompanyInfoView from '@routes/CompanyInfo/CompanyInfoView';
import {
  ReceivedQuotationNormalForm,
  ReceivedQuotationDesignForm
} from '@components/Form';
import { colorSet, theme } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import useStyles from '../../useStyles';
import { TabState } from './useActiveTab';
import { QuotationCommonWrapper } from '@components/Form/ReceivedQuotationForm';
import { NoQuotation } from '@assets';
import { Image } from '@components/Image';
import { useSelector } from '@store';

interface Props {
  isDesign: boolean;
  matches: boolean;
  haveConsulted: boolean;
  latestQuotation?: CapaQuotationDetailedQuotation;
  isClosedConsulting: boolean;
  partnerId: string;
  rfqId: string;
  activeTab: TabState;
  onChangeTab: (_: TabState) => void;
}

const companyInfoTheme = createTheme({
  ...theme,
  breakpoints: {
    values: {
      xs: 0,
      sm: 9999,
      md: 9999,
      lg: 9999,
      xl: 9999
    }
  }
});

const QuotationDetailPresenter = ({
  matches,
  isDesign,
  rfqId,
  haveConsulted,
  partnerId,
  latestQuotation,
  isClosedConsulting,
  activeTab,
  onChangeTab
}: Props) => {
  const classes = useStyles();
  const { onInitSelectedQuotation, onUpdateSelectedQuotation } = useDashboard();
  const history = useHistory();
  const matchesMd = useMediaQuery(theme.breakpoints.down('md'));

  const onBack = () => {
    history.push('/dashboard');

    if (matches) {
      onUpdateSelectedQuotation({
        chatRoomId: undefined,
        quotationId: undefined,
        quotationType: 'REQUEST',
        partnerId: undefined,
        rfqId
      });
    } else {
      onInitSelectedQuotation();
    }
  };

  const isWithdrawn =
    latestQuotation?.supply?.quotationPartnerStatus ===
      CapaQuotationPartnerStatus.Deleted ||
    latestQuotation?.supply?.quotationPartnerStatus === null;

  const isOnlyIntroduction = useSelector(
    (state) => state.dashboard.selectedQuotationId?.isOnlyIntroduction
  );

  const organizationName = useSelector(
    (state) => state.dashboard.selectedQuotationId?.organizationName
  );

  return (
    <>
      <Box display={matches ? 'block' : 'flex'} justifyContent={'center'}>
        <div className={classes['Dashboard-Right-Detail-Root']}>
          {matchesMd && (
            <div className={classes['Dashboard-Right-Detail-Mobile-Header']}>
              <IconButton onClick={onBack}>
                <ChevronLeft size={24} color={colorSet.gray900} />
              </IconButton>
              <CustomizedTypography
                variant="body1"
                fontWeight="500"
                color="textPrimary"
                className="mobile-header-organization-name"
              >
                {organizationName}
              </CustomizedTypography>
            </div>
          )}
          <Tabs
            className={classes['Dashboard-Right-Detail-Tabs']}
            value={activeTab}
            onChange={(_, newValue) => {
              if (newValue === 'quotation') {
                onChangeTab('quotation');
              } else {
                onChangeTab('partnerInfo');
              }
            }}
          >
            {!isWithdrawn && <Tab value={'partnerInfo'} label="파트너정보" />}
            <Tab value={'quotation'} label="견적서" />
          </Tabs>
          {activeTab === 'partnerInfo' && (
            <ThemeProvider theme={companyInfoTheme}>
              <div className={classes['companyinfoRequest']}>
                <CompanyInfoView partnerId={partnerId} isRequest />
              </div>
            </ThemeProvider>
          )}
          {activeTab === 'quotation' && (
            <div className={classes['Quotation-root']}>
              {isOnlyIntroduction ? (
                <div className={classes['no-quotation-wrapper']}>
                  <Image src={NoQuotation} className="no-quotation-image" />
                  <CustomizedTypography variant="body1" color="textSecondary">
                    견적서를 수령하지 않고 회사소개서만 확인 후 시작한
                    상담입니다.
                    <br />
                    견적서가 필요하다면 상담 후 견적서 발행을 요청해 주세요.
                  </CustomizedTypography>
                </div>
              ) : (
                <div>
                  <QuotationCommonWrapper
                    projectName={latestQuotation?.projectName ?? undefined}
                    createdAt={latestQuotation?.createdAt ?? ''}
                    isLatestQuotation={latestQuotation?.latest ?? false}
                    isWithdraw={isWithdrawn}
                    isClosedConsulting={isClosedConsulting}
                    rfqId={rfqId}
                    quotationId={latestQuotation?.id}
                    partnerId={partnerId}
                  >
                    {isDesign ? (
                      <ReceivedQuotationDesignForm
                        {...latestQuotation}
                        matches={matches}
                        rfqId={rfqId}
                        haveConsulted={haveConsulted}
                      />
                    ) : (
                      <ReceivedQuotationNormalForm
                        {...latestQuotation}
                        matches={matches}
                        rfqId={rfqId}
                        haveConsulted={haveConsulted}
                      />
                    )}
                  </QuotationCommonWrapper>
                </div>
              )}
            </div>
          )}
        </div>
      </Box>
    </>
  );
};

export default QuotationDetailPresenter;
